import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";

interface DuoRunModeProps {
  encounterId: string;
  myLocation: { lat: number; lng: number };
  partnerLocation: { lat: number; lng: number };
}

export const DuoRunMode = ({
  encounterId,
  myLocation,
  partnerLocation,
}: DuoRunModeProps) => {
  const [syncPercentage, setSyncPercentage] = useState(0);
  const [distance, setDistance] = useState(0);
  const [showSyncMessage, setShowSyncMessage] = useState(false);

  useEffect(() => {
    // Calcular distância entre corredores
    const dist = calculateDistance(
      myLocation.lat,
      myLocation.lng,
      partnerLocation.lat,
      partnerLocation.lng
    );
    setDistance(dist);

    // Calcular sincronia (quanto mais próximos, maior a sincronia)
    const maxDistance = 50; // 50m
    const sync = Math.max(0, Math.min(100, ((maxDistance - dist) / maxDistance) * 100));
    setSyncPercentage(Math.round(sync));

    // Mostrar mensagem quando sincronia > 80%
    if (sync > 80 && !showSyncMessage) {
      setShowSyncMessage(true);
      // TODO: Tocar áudio "Vocês estão em sincronia!"
      setTimeout(() => setShowSyncMessage(false), 3000);
    }
  }, [myLocation, partnerLocation, showSyncMessage]);

  return (
    <div className="fixed bottom-24 inset-x-0 z-40 flex justify-center px-4">
      <Card className="w-full max-w-md p-4 bg-card/95 backdrop-blur-lg border-primary/20">
        <div className="text-center mb-3">
          <h3 className="text-lg font-bold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            🏃‍♂️💨🏃‍♀️ Duo Run
          </h3>
          <p className="text-sm text-muted-foreground">
            Distância: {distance.toFixed(0)}m
          </p>
        </div>

        <div className="mb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Sincronia</span>
            <span className={`text-2xl font-bold ${
              syncPercentage > 80 ? 'text-green-500' : 
              syncPercentage > 60 ? 'text-primary' : 
              'text-red-500'
            }`}>
              {syncPercentage}%
            </span>
          </div>
          <Progress 
            value={syncPercentage} 
            className={`h-3 ${syncPercentage > 80 ? 'animate-pulse-soft' : ''}`}
          />
        </div>

        {showSyncMessage && (
          <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg animate-slide-up">
            <p className="text-center text-sm font-semibold text-green-600 dark:text-green-400">
              🎉 Vocês estão em sincronia perfeita!
            </p>
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-3 text-center text-xs">
          <div className="p-2 bg-primary/10 rounded-lg">
            <div className="font-semibold text-primary">Você</div>
            <div className="text-muted-foreground">📍</div>
          </div>
          <div className="p-2 bg-secondary/10 rounded-lg">
            <div className="font-semibold text-secondary">Parceiro</div>
            <div className="text-muted-foreground">📍</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}
